import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MessageSquare,
  Search,
  RefreshCw,
  User as UserIcon,
  Sparkles,
  ChevronRight,
  FileText,
  Loader2,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Download a PDF endpoint via the auth-aware api client, then trigger save.
export const downloadPdf = async (path, fallbackName) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const m = cd.match(/filename="([^"]+)"/);
    const fname = star ? decodeURIComponent(star[1]) : m ? m[1] : fallbackName;
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return true;
  } catch {
    return false;
  }
};

// Shared markdown component overrides — Tailwind preflight strips default
// heading styles, so we re-establish hierarchy here.
export const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="font-display text-xl font-bold mt-3 mb-2 tracking-tight text-white">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="font-display text-lg font-bold mt-4 mb-2 tracking-tight text-emerald-300">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-display text-base font-bold mt-3 mb-1.5 tracking-tight text-white">
      {children}
    </h3>
  ),
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
  p: ({ children }) => <p className="my-2">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  code: ({ inline, className, children, ...props }) =>
    inline ? (
      <code
        className="px-1.5 py-0.5 rounded bg-zinc-800/80 border border-zinc-700/60 text-emerald-300 text-[0.9em] font-mono"
        {...props}
      >
        {children}
      </code>
    ) : (
      <pre className="my-3 rounded-lg bg-zinc-950 border border-zinc-800 p-3 overflow-x-auto text-[12px]">
        <code className={`font-mono text-emerald-200 ${className || ""}`} {...props}>
          {children}
        </code>
      </pre>
    ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="min-w-full text-sm border border-zinc-800 rounded-lg overflow-hidden">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="text-left px-3 py-2 bg-zinc-900/60 border-b border-zinc-800 font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 border-b border-zinc-900">{children}</td>
  ),
};

export default function AdminConversations() {
  const [tools, setTools] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeToolId, setActiveToolId] = useState("all");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Load monitored-tool list once
  useEffect(() => {
    api
      .get("/admin/monitored-tools")
      .then((r) => setTools(r.data))
      .catch(() => toast.error("Failed to load monitored tools"));
  }, []);

  const loadSessions = useCallback(() => {
    setLoadingList(true);
    const params = { _: Date.now() };
    if (activeToolId !== "all") params.tool_id = activeToolId;
    if (search.trim()) params.q = search.trim();
    return api
      .get("/admin/monitored-sessions", { params })
      .then((r) => setSessions(r.data))
      .catch(() => toast.error("Failed to load conversations"))
      .finally(() => setLoadingList(false));
  }, [activeToolId, search]);

  // Initial + debounced reload when filters change
  useEffect(() => {
    const t = setTimeout(loadSessions, 250);
    return () => clearTimeout(t);
  }, [loadSessions]);

  const loadDetail = useCallback((sid) => {
    setSelectedId(sid);
    setDetail(null);
    setSummary(null);
    setDetailLoading(true);
    api
      .get(`/admin/monitored-sessions/${sid}`, { params: { _: Date.now() } })
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Failed to load conversation"))
      .finally(() => setDetailLoading(false));
  }, []);

  const loadSummary = useCallback(
    (force = false) => {
      if (!selectedId) return;
      setSummaryLoading(true);
      api
        .get(`/admin/monitored-sessions/${selectedId}/summary`, {
          params: { _: Date.now(), ...(force ? { refresh: true } : {}) },
        })
        .then((r) => setSummary(r.data))
        .catch(() => toast.error("Failed to generate summary"))
        .finally(() => setSummaryLoading(false));
    },
    [selectedId],
  );

  // Auto-generate summary the first time a session is opened (uses cache if available)
  useEffect(() => {
    if (detail && !summary && !summaryLoading) {
      loadSummary(false);
    }
  }, [detail, summary, summaryLoading, loadSummary]);

  const empty = useMemo(() => !loadingList && sessions.length === 0, [loadingList, sessions]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 h-[calc(100vh-260px)] min-h-[520px]">
      {/* ===== Left: filters + session list ===== */}
      <aside className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0">
        <div className="p-4 border-b border-zinc-900 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80">
              monitored chats
            </div>
            <button
              data-testid="conv-refresh-btn"
              onClick={loadSessions}
              className="text-zinc-500 hover:text-emerald-300 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Tool filter chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              data-testid="conv-filter-all"
              onClick={() => setActiveToolId("all")}
              className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border transition-colors ${
                activeToolId === "all"
                  ? "bg-emerald-600 border-emerald-500 text-white"
                  : "bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700"
              }`}
            >
              All ({tools.length})
            </button>
            {tools.map((t) => (
              <button
                key={t.id}
                data-testid={`conv-filter-${t.id}`}
                onClick={() => setActiveToolId(t.id)}
                title={t.name}
                className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border transition-colors truncate max-w-[160px] ${
                  activeToolId === t.id
                    ? "bg-emerald-600 border-emerald-500 text-white"
                    : "bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700"
                }`}
              >
                {t.name.replace(/^Business\s/i, "B. ")}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              data-testid="conv-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user name or email..."
              className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-emerald-500/50"
            />
          </div>
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto">
          {loadingList && (
            <div className="text-xs text-zinc-500 p-6 text-center">Loading...</div>
          )}
          {empty && (
            <div className="text-xs text-zinc-500 p-6 text-center">
              No conversations match these filters.
            </div>
          )}
          {!loadingList &&
            sessions.map((s) => (
              <button
                key={s.session_id}
                data-testid={`conv-row-${s.session_id}`}
                onClick={() => loadDetail(s.session_id)}
                className={`group w-full text-left px-4 py-3 border-b border-zinc-900 transition-colors ${
                  selectedId === s.session_id
                    ? "bg-emerald-500/10 border-l-2 border-l-emerald-400"
                    : "hover:bg-zinc-900/60"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 truncate">
                    {s.tool?.name || "—"}
                  </span>
                  <span className="text-[10px] text-zinc-600 shrink-0">
                    {fmtDate(s.updated_at)}
                  </span>
                </div>
                <div className="text-sm text-white truncate font-medium">
                  {s.title}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 truncate">
                    <UserIcon className="w-3 h-3 shrink-0 text-zinc-500" />
                    <span className="truncate">
                      {s.user?.name || s.user?.email || "Unknown user"}
                    </span>
                  </div>
                  <span className="text-[10px] text-zinc-500 shrink-0 inline-flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> {s.message_count}
                  </span>
                </div>
              </button>
            ))}
        </div>
      </aside>

      {/* ===== Right: transcript + summary ===== */}
      <section
        className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0"
        data-testid="conv-detail-pane"
      >
        {!selectedId ? (
          <EmptyState />
        ) : detailLoading || !detail ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading conversation...
          </div>
        ) : (
          <ConversationDetail
            detail={detail}
            summary={summary}
            summaryLoading={summaryLoading}
            onRegenerateSummary={() => loadSummary(true)}
          />
        )}
      </section>
    </div>
  );
}

const EmptyState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
    <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
      <MessageSquare className="w-6 h-6 text-emerald-300" />
    </div>
    <h3 className="font-display text-2xl tracking-tighter font-black text-white">
      Select a conversation
    </h3>
    <p className="text-zinc-500 text-sm mt-2 max-w-sm">
      Pick a chat from the list to read the full transcript and an
      auto-generated executive summary.
    </p>
  </div>
);

const ConversationDetail = ({ detail, summary, summaryLoading, onRegenerateSummary }) => {
  const { session, tool, user, messages } = detail;
  const [downloading, setDownloading] = useState(null); // 'transcript' | 'summary' | null

  const handleDownload = async (kind) => {
    if (downloading) return;
    setDownloading(kind);
    const ok = await downloadPdf(
      `/admin/monitored-sessions/${session.id}/download/${kind}`,
      `${kind}-${session.id.slice(0, 8)}.pdf`,
    );
    setDownloading(null);
    if (!ok) toast.error(`Failed to download ${kind}`);
    else toast.success(`${kind === "summary" ? "Summary" : "Transcript"} downloaded`);
  };

  return (
    <>
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-900 bg-zinc-950/60">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80 truncate">
            {tool?.name}
            <ChevronRight className="w-3 h-3 inline mx-1 -mt-0.5" />
            {session?.title}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="conv-download-summary"
              onClick={() => handleDownload("summary")}
              disabled={!!downloading}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-300 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-40"
              title="Download executive summary as PDF"
            >
              {downloading === "summary" ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
              Summary
            </button>
            <button
              data-testid="conv-download-transcript"
              onClick={() => handleDownload("transcript")}
              disabled={!!downloading}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-300 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-40"
              title="Download full chat transcript (with summary) as PDF"
            >
              {downloading === "transcript" ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
              Transcript
            </button>
            <span className="text-[10px] text-zinc-600">{fmtDate(session?.updated_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <div className="inline-flex items-center gap-1.5">
            <UserIcon className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-white">{user?.name || "—"}</span>
            {user?.email && (
              <span className="text-zinc-500">· {user.email}</span>
            )}
          </div>
          {user?.business_name && (
            <span className="text-zinc-500">· {user.business_name}</span>
          )}
          <span className="text-zinc-600">
            · {messages.length} message{messages.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* Scrollable body: summary + transcript */}
      <div className="flex-1 overflow-y-auto">
        {/* Summary block */}
        <section
          data-testid="conv-summary-block"
          className="px-5 md:px-8 py-6 border-b border-zinc-900 bg-emerald-500/[0.03]"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-300">
                Executive Summary
              </h4>
              {summary?.cached && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 border border-zinc-800 rounded-full px-2 py-0.5">
                  cached
                </span>
              )}
            </div>
            <button
              data-testid="conv-regen-summary"
              onClick={onRegenerateSummary}
              disabled={summaryLoading}
              className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/10 px-3 py-1.5 rounded-md transition-colors disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {summaryLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              {summaryLoading ? "Generating..." : "Regenerate"}
            </button>
          </div>

          {!summary && summaryLoading && (
            <div className="text-xs text-zinc-500 inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Generating summary…
            </div>
          )}
          {summary && (
            <div className="markdown-body text-[14px] leading-relaxed text-zinc-200">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {summary.summary}
              </ReactMarkdown>
            </div>
          )}
        </section>

        {/* Transcript */}
        <section className="px-5 md:px-8 py-6 space-y-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Full transcript
          </div>
          {messages.map((m) => (
            <TranscriptMessage key={m.id} msg={m} toolName={tool?.name} />
          ))}
        </section>
      </div>
    </>
  );
};

const TranscriptMessage = ({ msg, toolName }) => {
  const isUser = msg.role === "user";
  return (
    <div className="flex gap-3" data-testid={`conv-msg-${msg.id}`}>
      <div
        className={`w-8 h-8 shrink-0 rounded-lg border flex items-center justify-center mt-0.5 ${
          isUser
            ? "bg-zinc-800/60 border-zinc-700 text-zinc-300"
            : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
        }`}
      >
        {isUser ? (
          <UserIcon className="w-3.5 h-3.5" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            {isUser ? "User" : toolName || "Assistant"}
          </span>
          <span className="text-[10px] text-zinc-600">{fmtDate(msg.timestamp)}</span>
        </div>
        <div
          className={`rounded-xl px-4 py-3 text-[14px] leading-relaxed ${
            isUser
              ? "bg-zinc-800/40 border border-zinc-800 text-white whitespace-pre-wrap"
              : "bg-zinc-950/40 border border-zinc-900 text-zinc-200 markdown-body"
          }`}
        >
          {isUser ? (
            msg.content
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
};
